import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, PackagePlus, ShoppingCart, UserPlus, type LucideIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { usePermissions } from '@/hooks/usePermissions';
import { ownerPath } from '@/lib/hosts';

interface QuickActionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenCustomerWizard: () => void;
  onOpenStockWizard: () => void;
}

interface QuickActionItem {
  id: string;
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  href?: string;
  permission?: string;
}

const QUICK_ACTIONS: QuickActionItem[] = [
  {
    id: 'new_sale',
    icon: ShoppingCart,
    titleKey: 'quick_action_new_sale',
    descriptionKey: 'quick_action_new_sale_desc',
    href: '/pos',
    permission: 'sales.view',
  },
  {
    id: 'add_product',
    icon: PackagePlus,
    titleKey: 'quick_action_add_product',
    descriptionKey: 'quick_action_add_product_desc',
    href: '/products/create?mobile=1',
    permission: 'products.view',
  },
  {
    id: 'add_customer',
    icon: UserPlus,
    titleKey: 'quick_action_add_customer',
    descriptionKey: 'quick_action_add_customer_desc',
    permission: 'customers.view',
  },
  {
    id: 'stock_adjustment',
    icon: Box,
    titleKey: 'quick_action_stock_adjustment',
    descriptionKey: 'quick_action_stock_adjustment_desc',
    permission: 'stock.view',
  },
];

export function QuickActionSheet({
  open,
  onOpenChange,
  onOpenCustomerWizard,
  onOpenStockWizard,
}: QuickActionSheetProps) {
  const { t } = useTranslation('navigation');
  const { can } = usePermissions();
  const navigate = useNavigate();

  const visibleActions = useMemo(
    () => QUICK_ACTIONS.filter((item) => !item.permission || can(item.permission)),
    [can],
  );

  const handleAction = (action: QuickActionItem) => {
    onOpenChange(false);
    if (action.id === 'stock_adjustment') {
      onOpenStockWizard();
    } else if (action.id === 'add_customer') {
      onOpenCustomerWizard();
    } else if (action.href) {
      navigate(ownerPath(action.href));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[80vh] rounded-t-2xl border-t border-slate-200 bg-white p-0 shadow-[0_-12px_32px_-12px_rgba(15,23,42,0.18)] dark:border-charcoal-800 dark:bg-card"
        showCloseButton={false}
        style={{ paddingBottom: 'calc(var(--bottom-tab-safe-area) + 16px)' }}
      >
        <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-charcoal-700" />
        <SheetHeader className="space-y-1 px-5 pb-4 pt-4 dark:border-charcoal-800">
          <SheetTitle className="text-left text-[16px] font-semibold text-charcoal-950 dark:text-charcoal-50">
            {t('quick_actions_title')}
          </SheetTitle>
          <SheetDescription className="text-left text-[13px] text-slate-500 dark:text-charcoal-300">
            {t('quick_actions_description')}
          </SheetDescription>
        </SheetHeader>

        <div className="grid grid-cols-2 gap-3 border-t border-slate-200 px-5 pt-4 dark:border-charcoal-800">
          {visibleActions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => handleAction(action)}
              className="group flex min-h-28 flex-col rounded-lg border border-slate-200 bg-white p-3.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_1px_rgba(15,23,42,0.02)] transition-all duration-200 hover:border-slate-300 hover:bg-slate-900/[.02] hover:shadow-[0_8px_24px_-8px_rgba(15,23,42,0.12)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 dark:border-charcoal-700 dark:bg-card dark:hover:bg-charcoal-800 dark:focus-visible:ring-offset-charcoal-900"
            >
              <div className="mb-3 grid h-9 w-9 place-items-center rounded-md bg-lime-100 text-lime-700 dark:bg-lime-400/15 dark:text-lime-300">
                <action.icon className="h-[18px] w-[18px]" strokeWidth={2} />
              </div>
              <p className="text-[14px] font-semibold leading-tight text-charcoal-950 dark:text-charcoal-50">
                {t(action.titleKey)}
              </p>
              <p className="mt-1 text-[12px] leading-snug text-slate-500 dark:text-charcoal-300">
                {t(action.descriptionKey)}
              </p>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
